import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { usePublicTheme } from '@/context/PublicThemeContext';
import { MapPin, Clock, Ruler, Mountain, CalendarCheck, ChevronLeft, ChevronRight, Camera, Compass } from 'lucide-react';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=1000&q=80';

// Mini-gallery SPECULARE al punto: solo immagini impostate sul POI (nessun mix con l'area)
const buildGallery = (point) => {
  const imgs = [];
  const push = (u) => { if (u && typeof u === 'string' && u.trim()) imgs.push(u.trim()); };
  if (Array.isArray(point?.gallery)) point.gallery.forEach(push);
  push(point?.photo);
  return [...new Set(imgs)];
};

export const PoiDialog = ({ open, onOpenChange, point, area, onBook }) => {
  const ctx = usePublicTheme();
  const theme = ctx?.theme || 'light';
  const rawGallery = useMemo(() => buildGallery(point), [point]);
  const gallery = rawGallery.length ? rawGallery : [PLACEHOLDER];
  const [idx, setIdx] = useState(0);

  // reset dell'immagine attiva quando cambia il POI
  useEffect(() => { setIdx(0); }, [point, area]);

  const hasMany = gallery.length > 1;
  const go = (delta) => setIdx((i) => (i + delta + gallery.length) % gallery.length);

  const isLuogo = area?.tipo === 'luogo';
  const title = point?.name || area?.name || 'Punto del Parco';
  // SPECULARE: mostra esattamente la descrizione del punto (nessun fallback all'area)
  const desc = (point?.description || '').trim();

  const chips = isLuogo ? [] : [
    area?.duration ? { Icon: Clock, label: area.duration } : null,
    area?.length_km ? { Icon: Ruler, label: `${area.length_km} km` } : null,
    area?.difficulty ? { Icon: Mountain, label: area.difficulty } : null,
  ].filter(Boolean);
  const showPrice = !isLuogo && area?.price != null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sila-public ${theme === 'dark' ? 'dark' : ''} bg-background text-foreground sm:max-w-lg p-0 overflow-hidden`} data-testid="poi-dialog">
        {/* HERO / immagine grande della mini-gallery */}
        <div className="relative h-64 w-full overflow-hidden bg-[hsl(var(--brand-bark)/0.10)]">
          <img
            src={gallery[idx]}
            alt={title}
            className="h-full w-full object-cover"
            onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
            data-testid="poi-dialog-hero-image"
          />
          <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0.55) 100%)' }} />
          <div aria-hidden className="grain-overlay absolute inset-0 opacity-40" />

          {/* badge itinerario */}
          <span className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/35 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur-md">
            <Compass className="h-3.5 w-3.5" style={{ color: isLuogo ? '#f5c518' : (area?.color || 'hsl(var(--brand-sun))') }} />
            {isLuogo ? 'Luogo del Parco' : (area?.name || 'Itinerario')}
          </span>

          {/* contatore + frecce mini-gallery */}
          {hasMany && (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="Foto precedente"
                data-testid="poi-gallery-prev"
                className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur transition-colors duration-200 hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="Foto successiva"
                data-testid="poi-gallery-next"
                className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur transition-colors duration-200 hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <span className="absolute bottom-3 right-4 inline-flex items-center gap-1.5 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur">
                <Camera className="h-3 w-3" />{idx + 1}/{gallery.length}
              </span>
            </>
          )}

          {/* titolo in overlay */}
          <div className="absolute inset-x-0 bottom-0 p-5">
            <h3 className="font-display text-2xl font-bold leading-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]" data-testid="poi-dialog-title-overlay">{title}</h3>
          </div>
        </div>

        {/* Striscia thumbnails della mini-gallery */}
        {hasMany && (
          <div className="flex gap-2 overflow-x-auto border-b border-border px-4 py-3" data-testid="poi-gallery-thumbs">
            {gallery.map((g, i) => (
              <button
                key={`${g}-${i}`}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Vai alla foto ${i + 1}`}
                data-testid={`poi-gallery-thumb-${i}`}
                className={`relative h-14 w-20 shrink-0 overflow-hidden rounded-lg ring-2 transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none ${idx === i ? 'ring-primary' : 'ring-transparent opacity-70 hover:opacity-100'}`}
              >
                <img src={g} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
              </button>
            ))}
          </div>
        )}

        {/* Contenuto */}
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="sr-only">{title}</DialogTitle>
            {desc ? (
              <>
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 text-primary" />Cosa vedere
                </p>
                <DialogDescription className="mt-2 text-[15px] leading-relaxed text-muted-foreground" data-testid="poi-dialog-description">
                  {desc}
                </DialogDescription>
              </>
            ) : (
              <DialogDescription className="sr-only">{title}</DialogDescription>
            )}
          </DialogHeader>

          {/* chips info itinerario */}
          {(chips.length > 0 || showPrice) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {chips.map(({ Icon, label }, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground">
                  <Icon className="h-3.5 w-3.5 text-primary" />{label}
                </span>
              ))}
              {showPrice && (
                <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-testid="poi-dialog-price">
                  €{area.price} <span className="ml-1 font-normal text-primary/70">/pers.</span>
                </span>
              )}
            </div>
          )}

          {/* CTA */}
          <div className="mt-6 flex flex-wrap gap-3">
            {isLuogo ? (
              <Button className="flex-1 rounded-full" onClick={() => onOpenChange(false)} data-testid="poi-dialog-close">
                Chiudi
              </Button>
            ) : (
              <>
                <Button className="flex-1 rounded-full" onClick={onBook} data-testid="poi-dialog-book">
                  <CalendarCheck className="mr-2 h-4 w-4" />Prenota questo tour
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="poi-dialog-close">
                  Chiudi
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PoiDialog;
